import React, { useState, useEffect, useRef } from 'react';
import { JsonViewer } from '@textea/json-viewer';
import { LineNumberedTextArea } from './LineNumberedTextArea';

const JSONViewer: React.FC = () => {
  const [jsonInput, setJsonInput] = useState('');
  const [jsonData, setJsonData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [lineCount, setLineCount] = useState(1);
  const outputRef = useRef<HTMLDivElement>(null);
  const lineNumbersRef = useRef<HTMLDivElement>(null);
  const [isFormatted, setIsFormatted] = useState(false);

  useEffect(() => {
    if (isFormatted) {
      updateLineCount();
    }
  }, [jsonData, isFormatted]);

  const countJsonLines = (obj: any, depth = 1): number => {
    if (!obj) return 1;
    
    let lines = 1; // Opening brace
    
    if (typeof obj === 'object') {
      const entries = Array.isArray(obj) ? obj : Object.entries(obj);
      
      entries.forEach((item, index) => {
        if (Array.isArray(obj)) {
          if (typeof item === 'object') {
            lines += countJsonLines(item, depth + 1);
          } else {
            lines += 1;
          }
        } else {
          const [, value] = item;
          if (typeof value === 'object') {
            lines += 1; // Key
            lines += countJsonLines(value, depth + 1);
          } else {
            lines += 1; // Key-value pair
          }
        }
        
        if (index < entries.length - 1) {
          lines += 1; // Comma and newline
        }
      });
      
      lines += 1; // Closing brace
    }
    
    return lines;
  };

  const updateLineCount = () => {
    if (jsonData) {
      const count = countJsonLines(jsonData);
      setLineCount(count);
      
      // Force a reflow of line numbers
      if (lineNumbersRef.current) {
        lineNumbersRef.current.style.display = 'none';
        lineNumbersRef.current.offsetHeight; // Force reflow
        lineNumbersRef.current.style.display = '';
      }
    } else {
      setLineCount(1);
    }
  };

  // const formatJSON = (input: string) => {
  //   if (!input.trim()) {
  //     setJsonData(null);
  //     setError(null);
  //     setJsonInput('');
  //     setLineCount(1);
  //     setIsFormatted(false);
  //     return;
  //   }

  //   try {
  //     const parsed = JSON.parse(input);
  //     const formatted = JSON.stringify(parsed, null, 2);
  //     setJsonInput(formatted);
  //     setJsonData(parsed);
  //     setError(null);
  //     setIsFormatted(true);
  //   } catch (e) {
  //     setJsonData(null);
  //     setError((e as Error).message);
  //     setLineCount(1);
  //     setIsFormatted(false);
  //   }
  // };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setJsonInput(value);
    setIsFormatted(false);
    
    if (value.trim() === '') {
      setJsonData(null);
      setError(null);
      setLineCount(1);
      return;
    }

    const timeoutId = setTimeout(() => {
      try {
        const parsed = JSON.parse(value);
        setJsonData(parsed);
        setError(null);
        const formatted = JSON.stringify(parsed, null, 2);
        if (formatted !== value) {
          setJsonInput(formatted);
        }
        setIsFormatted(true);
      } catch (e) {
        setJsonData(null);
        setError((e as Error).message);
        setLineCount(1);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (lineNumbersRef.current) {
      lineNumbersRef.current.style.transform = `translateY(-${e.currentTarget.scrollTop}px)`;
    }
  };

  // Generate line numbers for output
  const lineNumbers = Array.from({ length: lineCount }, (_, i) => (
    <div key={i + 1} className="line-number">{i + 1}</div>
  ));

  return (
    <div className="json-viewer flex flex-col lg:flex-row h-full gap-4 p-4">
      <div className="input-section w-full lg:w-1/2">
        <div className="section-header">JSON Input</div>
        <div className="flex-1 relative">
          <LineNumberedTextArea
            value={jsonInput}
            onChange={handleInputChange}
            placeholder="Enter JSON here..."
          />
        </div>
        {error && <div className="error-message">{error}</div>}
      </div>
      
      <div className="viewer-section w-full lg:w-1/2">
        <div className="section-header">Formatted Output</div>
        <div className="flex-1 relative">
          <div className="absolute left-0 top-0 bottom-0 w-[40px] sm:w-[50px] bg-editor-dark border-r border-editor-border z-10">
            <div ref={lineNumbersRef} className="absolute top-0 left-0 w-full py-2 font-mono text-xs sm:text-sm text-editor-line text-right select-none">
              {lineNumbers}
            </div>
          </div>
          <div 
            ref={outputRef}
            onScroll={handleScroll}
            className="absolute inset-0 overflow-auto"
          >
            <div className="min-h-full pl-[45px] sm:pl-[58px] pr-2 py-2">
              {jsonData && (
                <JsonViewer 
                  value={jsonData}
                  defaultInspectDepth={5}
                  rootName={false}
                  displayDataTypes={false}
                  enableClipboard={true}
                  theme={{
                    base00: '#1e1e1e',
                    base01: '#262626',
                    base02: '#303030',
                    base03: '#666666',
                    base04: '#808080',
                    base05: '#a0a0a0',
                    base06: '#c0c0c0',
                    base07: '#ffffff',
                    base08: '#ff5555',
                    base09: '#ff9955',
                    base0A: '#ffff55',
                    base0B: '#55ff55',
                    base0C: '#55ffff',
                    base0D: '#5555ff',
                    base0E: '#ff55ff',
                    base0F: '#ff5555'
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JSONViewer;
